import useAllertEdit from '@package/hooks/src/useAllertEdit';
import useElementSize from '@package/hooks/src/useElementSize';
import useMenu from '@package/hooks/src/useMenu';
import clsx from 'clsx';
import config, { LayoutType } from 'config-base';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { EditIcon } from '@base/components/src/Icon';
import Menu from '@base/components/src/Menu';
import SearchComponent from '@base/components/src/SearchComponent';
import AppBarPopup from '@base/components/src/AppBarPopup';
import CartPopup from '@base/components/src/CartPopup';
import NextLink from '@base/components/src/Link/NextLink';
import NextImage from 'next/image';
import { HeaderProps } from './types';

import styles from './Header.module.css';

const SellerCartIcon = dynamic(() => import('@base/components/src/SellerCartIcon'));
const TopBanner = dynamic(() => import('@base/components/src/TopBanner'));

const Header = ({
  bannerMessage,
  searchComponent,
  menuComponent,
  onCopySellerUrl,
  layoutType,
  showSellerUrl,
}: HeaderProps) => {
  const { menuData } = useMenu();
  const { allertEditUrl } = useAllertEdit('0', 'promoAlertMessageEdit');
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);
  const [topPosition, setTopPosition] = useState<number>(0);

  const [squareRef, { height }] = useElementSize();
  const [squareWithoutBannerRef, { height: withoutBannerHeight }] = useElementSize();

  const handleCartClick = () => {
    if (isMenuOpened) {
      setIsMenuOpened(false);
    }
  };

  return (
    <header id="header" ref={squareRef} className={styles.header}>
      {allertEditUrl && <EditIcon id="top-banner-edit" url={allertEditUrl} />}
      {bannerMessage && layoutType === LayoutType.Main && <TopBanner bannerMessage={bannerMessage} />}
      <section
        ref={squareWithoutBannerRef}
        className={clsx(styles.headerContainer, {
          [styles.checkoutHeader]: layoutType == LayoutType.Chekout,
        })}
      >
        <div className={clsx(styles.container, styles.wrapper)}>
          <div id="header-logo" className={styles.logo}>
            <NextLink
              data-testid={'header-logo-link'}
              showCursorPointer
              href={config.mainPyramydairUrl}
              passHref={true}
            >
              <NextImage
                src="/marketplace-static/images/pyramyd_logo_new.svg"
                width={258}
                height={40}
                alt="Pyramyd Adventures in Recreation"
              />
            </NextLink>
            {allertEditUrl && showSellerUrl && <SellerCartIcon onSellerUrl={onCopySellerUrl} />}
          </div>
          {searchComponent && (
            <SearchComponent
              id="header-search-input"
              topPosition={topPosition}
              handleSearchPosition={() => {
                setTopPosition(height);
              }}
              handleInputFocus={setIsMenuOpened}
              isMenuOpen={isMenuOpened}
            />
          )}
          <section className={styles.personal}>
            <AppBarPopup headerHeight={withoutBannerHeight} />
            <CartPopup topPosition={height} toggleVisible={handleCartClick} />
            {layoutType !== LayoutType.Chekout && (
              <div
                id="main-menu-icon"
                data-testid={'main-menu-icon'}
                className={clsx(styles.burger, { [styles.burgerActive]: isMenuOpened })}
                onClick={() => {
                  setIsMenuOpened(!isMenuOpened);
                  setTopPosition(height);
                }}
              ></div>
            )}
          </section>
        </div>
      </section>
      {menuComponent && menuData?.menu && (
        <Menu
          menuData={menuData?.menu}
          url={config.mainPyramydairUrl}
          isMenuOpen={isMenuOpened}
          handleOpenMenu={setIsMenuOpened}
          topPosition={topPosition}
        />
      )}
    </header>
  );
};

export default Header;
